import { TreeDataItem } from "@comps/ui/tree-view";
import { AggregationType, FieldType } from "@dto/fields";
import { ObjectInfos, SharedObjectInfos } from "@dto/objects";
import { ObjectRelation } from "@dto/relation";
import { idToKey } from "./types";
import { Direction, Edge, INBOUND, OUTBOUND, Path } from "@dto/records";
import { pathKey } from "./model";

export function buildFieldTree(objectInfos: ObjectInfos[] | undefined, onFieldClick: (id: string, fieldKey: string) => void): TreeDataItem[] {
    return objectInfos?.map(objectInfo => {
        const id = objectInfo.object._key;
        let name = objectInfo.object.name;
        const children = objectInfo.fields?.map(field => {
            return {
                id: field._key,
                name: field.name,
                children: undefined,
                onClick: () => onFieldClick(id, field._key)
            }
        })
        return {
            id: id,
            name: name,
            children: children?.filter(child => child !== null) as TreeDataItem[],
        }
    }) ?? [];
}

export function fillWithPathKeyToNameMap(objectTree: TreeDataItem[] | null, map: Map<string, string>, objectByKeys: Map<string, ObjectInfos>, relationByKeys: Map<string, ObjectRelation>) {
    objectTree?.forEach(child => {
        const treeValue = getTreePathValue(child.id, objectByKeys, relationByKeys);
        if (treeValue && treeValue.path && treeValue.path.field) {
            map.set(pathKey(treeValue.path), treeValue.name);
        }
        if (child.children) {
            fillWithPathKeyToNameMap(child.children, map, objectByKeys, relationByKeys);
        }
    });
}

export function fillWithNameToPathKeyMap(objectTree: TreeDataItem[] | null, map: Map<string, string>, objectByKeys: Map<string, ObjectInfos>, relationByKeys: Map<string, ObjectRelation>) {
    objectTree?.forEach(child => {
        const treeValue = getTreePathValue(child.id, objectByKeys, relationByKeys);
        if (treeValue && treeValue.path && treeValue.path.field) {
            map.set(treeValue.name, pathKey(treeValue.path));
        }
        if (child.children) {
            fillWithNameToPathKeyMap(child.children, map, objectByKeys, relationByKeys);
        }
    });
}

export function buildPathTree(objectInfos: ObjectInfos[] | undefined, sharedObjectInfos: SharedObjectInfos[] | undefined, objectKey: string, onPathClicked: (id: string) => void): TreeDataItem | null {
    return internalBuildPathTree(objectInfos, sharedObjectInfos, objectKey, null, null, new Set<string>(), onPathClicked);
}

function internalBuildPathTree(objectInfos: ObjectInfos[] | undefined, sharedObjectInfos: SharedObjectInfos[] | undefined, objectKey: string, previousId: string | null, relation: ObjectRelation | null, visited: Set<string>, onPathClicked: (id: string) => void): TreeDataItem | null {
    let objectInfo = objectInfos ? objectInfos.find(objectInfo => objectInfo.object._key === objectKey) : null;
    if (!objectInfo) {
        let found = false;
        if (sharedObjectInfos && sharedObjectInfos.length > 0) {
            for (let i = 0; i < sharedObjectInfos.length && !found; i++) {
                const sharedObjectInfo = sharedObjectInfos[i];
                if (sharedObjectInfo.objectInfos && sharedObjectInfo.objectInfos.length > 0) {
                    for (let j = 0; j < sharedObjectInfo.objectInfos.length && !found; j++) {
                        const objInfo = sharedObjectInfo.objectInfos[j];
                        if (objInfo.object._key === objectKey) {
                            objectInfo = objInfo;
                            found = true;
                        }
                    }
                }
            }
        };
    }
    if (objectInfo) {
        if (visited.has(objectKey)) {
            return null;
        }
        const newVisited = new Set<string>(visited);
        newVisited.add(objectKey);

        let id: string
        let name: string
        let children: TreeDataItem[] = [];
        if (!relation) {
            id = objectKey;
            name = objectInfo.object.name;

            objectInfo.fields?.forEach(field => {
                if (field.type != FieldType.Link) {
                    children.push({
                        id: objectKey + "/" + field._key,
                        name: field.name,
                        children: undefined,
                        onClick: () => onPathClicked(objectKey + "/" + field._key)
                    })
                }
            })

            objectInfo.relations?.forEach(relation => {
                let destObjectKey = idToKey(relation._to);
                if (destObjectKey == objectKey) {
                    destObjectKey = idToKey(relation._from);
                }
                const child = internalBuildPathTree(objectInfos, sharedObjectInfos, destObjectKey, id, relation, newVisited, onPathClicked);
                if (child) {
                    children.push(child);
                }
            })
        } else {
            id = previousId + "-" + relation._key;
            name = objectInfo.object.name;
            if (relation.name != objectInfo.object.name) {
                name += " (" + relation.name + ")";
            }

            objectInfo.fields?.forEach(field => {
                if (field.type != FieldType.Link) {
                    children.push({
                        id: id + "/" + field._key,
                        name: field.name,
                        children: undefined,
                        onClick: () => onPathClicked(id + "/" + field._key)
                    })
                }
            })

            objectInfo.relations?.forEach(relation => {
                let destObjectKey = idToKey(relation._to);
                if (destObjectKey == objectKey) {
                    destObjectKey = idToKey(relation._from);
                }
                const child = internalBuildPathTree(objectInfos, sharedObjectInfos, destObjectKey, id, relation, newVisited, onPathClicked);
                if (child) {
                    children.push(child);
                }
            })
        }
        return {
            id: id,
            name: name,
            children: children?.filter(child => child !== null) as TreeDataItem[]
        }
    }
    return null;
}


export function buildAllPathTrees(objectInfos: ObjectInfos[] | undefined, sharedObjectInfos: SharedObjectInfos[] | undefined, onPathClicked: (id: string) => void): TreeDataItem[] | null {
    return internalBuildAllPathTrees(objectInfos, sharedObjectInfos, null, null, new Set<string>(), onPathClicked);
}

function internalBuildAllPathTrees(objectInfos: ObjectInfos[] | undefined, sharedObjectInfos: SharedObjectInfos[] | undefined, previousId: string | null, relation: ObjectRelation | null, visited: Set<string>, onPathClicked: (id: string) => void): TreeDataItem[] | null {
    const AllObjectInfos: ObjectInfos[] = [];
    if (objectInfos) {
        AllObjectInfos.push(...objectInfos);
    }
    if (sharedObjectInfos) {
        for (let i = 0; i < sharedObjectInfos.length; i++) {
            const sharedObjectInfo = sharedObjectInfos[i];
            AllObjectInfos.push(...sharedObjectInfo.objectInfos);
        }
    }

    const result: TreeDataItem[] = [];
    for (let objectInfo of AllObjectInfos) {
        const currentObjectKey = objectInfo.object._key;
        if (visited.has(currentObjectKey)) {
            continue;
        }
        const newVisited = new Set<string>(visited);
        newVisited.add(currentObjectKey);

        let id: string
        let name: string
        let children: TreeDataItem[] = [];
        if (!relation) {
            id = currentObjectKey;
            name = objectInfo.object.name;

            objectInfo.fields?.forEach(field => {
                if (field.type != FieldType.Link) {
                    children.push({
                        id: currentObjectKey + "/" + field._key,
                        name: field.name,
                        children: undefined,
                        onClick: () => onPathClicked(currentObjectKey + "/" + field._key)
                    })
                }
            })

            objectInfo.relations?.forEach(relation => {
                let destObjectKey = idToKey(relation._to);
                if (destObjectKey == currentObjectKey) {
                    destObjectKey = idToKey(relation._from);
                }
                const child = internalBuildPathTree(objectInfos, sharedObjectInfos, destObjectKey, id, relation, newVisited, onPathClicked);
                if (child) {
                    children.push(child);
                }
            })
        } else {
            id = previousId + "-" + relation._key;
            name = objectInfo.object.name;
            if (relation.name != objectInfo.object.name) {
                name += " (" + relation.name + ")";
            }

            objectInfo.fields?.forEach(field => {
                if (field.type != FieldType.Link) {
                    children.push({
                        id: id + "/" + field._key,
                        name: field.name,
                        children: undefined,
                        onClick: () => onPathClicked(id + "/" + field._key)
                    })
                }
            })

            objectInfo.relations?.forEach(relation => {
                let destObjectKey = idToKey(relation._to);
                if (destObjectKey == currentObjectKey) {
                    destObjectKey = idToKey(relation._from);
                }
                const child = internalBuildPathTree(objectInfos, sharedObjectInfos, destObjectKey, id, relation, newVisited, onPathClicked);
                if (child) {
                    children.push(child);
                }
            })
        }
        result.push({
            id: id,
            name: name,
            children: children?.filter(child => child !== null) as TreeDataItem[]
        });
    }
    return result;
}

export type TreePathValue = {
    path: Path;
    name: string;
}

export function getTreePathValue(treeItemId: string | undefined, objectByKeys: Map<string, ObjectInfos>, relationByKeys: Map<string, ObjectRelation>, shortName: boolean = false): TreePathValue | undefined {

    if (!treeItemId) {
        return undefined;
    }

    const categoryParts = treeItemId.split("/");
    const categoryRoot = categoryParts[0];
    const categoryRelations = categoryRoot.split("-");
    const categoryFieldKey = categoryParts[1];

    let result: Path | null = null;
    let categoryPathName = "";
    if (categoryRelations.length == 1) {
        result = {
            edges: [],
            object: categoryRoot,
            field: categoryFieldKey,
            srcField: null,
            srcObject: null,
            aggregation: AggregationType.NO_AGGR
        }
        const categoryObject = objectByKeys?.get(categoryRoot);
        const categoryField = categoryObject?.fields?.find(field => field._key === categoryFieldKey);
        categoryPathName = categoryObject?.object.name + " > " + categoryField?.name;
    } else {
        let srcObject = categoryRelations[0];
        let destObjectKey = categoryRelations[0];
        const firstObjectKey = srcObject;
        let categoryObject = objectByKeys?.get(destObjectKey);
        let categoryField = categoryObject?.fields?.find(field => field._key === categoryFieldKey);
        categoryPathName = categoryObject?.object.name ?? "";
        const edges: Edge[] = [];
        for (let i = 1; i < categoryRelations.length; i++) {
            const relation = relationByKeys?.get(categoryRelations[i]);
            if (relation) {
                let direction: Direction = OUTBOUND;
                destObjectKey = idToKey(relation._to);
                if (destObjectKey == srcObject) {
                    direction = INBOUND;
                    destObjectKey = idToKey(relation._from);
                }
                edges.push({
                    key: relation._key,
                    direction: direction
                });
                srcObject = destObjectKey;
                categoryObject = objectByKeys?.get(destObjectKey);
                categoryField = categoryObject?.fields?.find(field => field._key === categoryFieldKey);
                const objName = categoryObject?.object.name ?? "";
                const relationSuffix = relation.name !== objName ? ` (${relation.name})` : "";
                if (shortName) {
                    categoryPathName += objName + relationSuffix;
                } else {
                    categoryPathName += " > " + objName + relationSuffix;
                }
            }
        }
        result = {
            edges: edges,
            object: destObjectKey,
            field: categoryFieldKey,
            srcField: null,
            srcObject: firstObjectKey,
            aggregation: AggregationType.NO_AGGR
        }
        categoryPathName += " > " + categoryField?.name;
    }
    return {
        path: result,
        name: categoryPathName
    }
}